import styled from 'styled-components';

import type { BasketState } from '@front/common/src/ducks/common/basket';
import type { User } from '@front/common/src/types/commons';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { formatPercentage, formatPricePerHour, formatPricePerMonth } from '@front/common/src/utils/formatting';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import useGetPrices from '@front/customer/src/hooks/use-get-prices';
import { getMembershipQuestions, type MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';

import { translate } from '@front/i18n';

import Title from '@front/web-ui/src/components/base-text/variants/title';
import Button from '@front/web-ui/src/components/button/button';
import { minWidth } from '@front/web-ui/src/styles/mixins';

import Advantages from '../advantages';
import {
  Content,
  MembershipBackground,
  MembershipFaq,
  PriceCardsContainer,
  StickyContainer,
  StyledStickyBottomWrapper,
  StyledWhiteSpacerRoundedBorders,
} from '../common';
import SelectableCleaningMemberPriceCard from './selectable-cleaning-member-price-card';
import SelectableCleaningStandardPriceCard from './selectable-cleaning-standard-price-card';

export const MEMBERSHIP_PAYWALL_CLEANING_PRICES_LOADED_TEST_ID = 'membership-paywall-cleaning-prices-loaded-test-id';

const t = translate('modals.membership_paywall');

const StyledTitle = styled(Title.LG)`
  margin: 20px 0;

  ${minWidth.md`
    margin: 0 0 20px;
  `}
`;

const StyledSpacer = styled.div`
  height: 20px;
`;

type SpotContentProps = {
  duration: BasketState['duration'];
  optInChoice?: MembershipOptIn;
  user?: User;
  onConfirmPaywall: () => void;
  onOptInChoiceSelection: (newOptInChoice: MembershipOptIn) => void;
};

const SpotContent = ({ duration, optInChoice, user, onConfirmPaywall, onOptInChoiceSelection }: SpotContentProps) => {
  const { prices, pricesError } = useGetPrices();
  const isLoadingPrices = !prices || !!pricesError;

  const currentCountry = getCurrentCountry();

  const membershipConfig = MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry];
  const formattedWellnessMemberReductionPercentage = formatPercentage(membershipConfig.wellnessReductionsPercentage);
  const formattedCleaningMemberHourlyReduction = formatPricePerHour(membershipConfig.cleaningMaxPricePerHour, {
    precision: 0,
  });
  const formattedMembershipPricePerMonth = formatPricePerMonth(membershipConfig.monthlyPrice);

  const advantages = [
    t('.advantages.wellness.prestation_reductions', {
      reductionsInPercentage: formattedWellnessMemberReductionPercentage,
    }),
    t('.advantages.cleaning.prestation_reductions', {
      pricePerHour: formattedCleaningMemberHourlyReduction,
    }),
    t('.advantages.wellness.free_month', { pricePerMonth: formattedMembershipPricePerMonth }),
    t('.advantages.wellness.cancel_anytime'),
  ];

  return (
    <StickyContainer>
      <Content>
        <MembershipBackground>
          <StyledTitle>{t('.title.cleaning.spot')}</StyledTitle>
          <Advantages advantages={advantages} />
        </MembershipBackground>
        <StyledWhiteSpacerRoundedBorders />
        <PriceCardsContainer data-testid={prices ? MEMBERSHIP_PAYWALL_CLEANING_PRICES_LOADED_TEST_ID : undefined}>
          <SelectableCleaningMemberPriceCard
            optInChoice={optInChoice}
            onPress={onOptInChoiceSelection}
            prices={prices}
            isLoadingPrices={isLoadingPrices}
            duration={duration}
          />
          <StyledSpacer />
          <SelectableCleaningStandardPriceCard
            optInChoice={optInChoice}
            onPress={onOptInChoiceSelection}
            prices={prices}
            isLoadingPrices={isLoadingPrices}
            duration={duration}
          />
        </PriceCardsContainer>
        <MembershipFaq
          title={t('.faq.title')}
          questions={getMembershipQuestions({
            universeReference: 'cleaning',
            isSpotCleaning: true,
            user,
          })}
        />
      </Content>
      <StyledStickyBottomWrapper>
        <Button size='lg' onPress={onConfirmPaywall} hasFullWidth isDisabled={!optInChoice}>
          {t('base.continue')}
        </Button>
      </StyledStickyBottomWrapper>
    </StickyContainer>
  );
};

export default SpotContent;
